// 全螢幕門鈴來電卡（spec 16 §2.2 room 廣播即時面;native 端對應 CallKit 全屏）。
// 全域唯一落點在 AppShell（<main> 內,scrim 蓋滿視窗）。store 見 doorbellRing.ts。
//
// 三動作皆收起卡片:
//   接聽 → 既有 WebRTC 即時頁 /devices/:id/live（web 的「接聽」= 看即時影像,誠實）
//   查看 → 該裝置事件時間軸 /events/:deviceId
//   關閉 → 略過（紅點仍由 liveEvents 計）
// Esc 同「關閉」。卡片本身無計時顯示;TTL 收起由 store 負責。

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSyncExternalStore } from 'react';
import { doorbellRing } from './doorbellRing';
import { answeredText, doorbellAnswered } from './doorbellAnswered';
import { doorbellLiveTarget, currentUserId } from '../devices/panels';
import { useFocusTrap } from '../../lib/useFocusTrap';
import './doorbell.css';

export function IncomingDoorbellCard() {
  const { t } = useTranslation();
  const ring = useSyncExternalStore(doorbellRing.subscribe, doorbellRing.get);
  useSyncExternalStore(doorbellAnswered.subscribe, doorbellAnswered.version);
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);

  // a11y: trap focus inside the card while it's up, restore on dismiss.
  useFocusTrap(!!ring, cardRef);

  // Esc = 關閉（卡片開啟期間才掛載）。
  useEffect(() => {
    if (!ring) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') doorbellRing.dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ring]);

  if (!ring) return null;

  // 進門口畫面再一鍵［接聽］（V-14：不直接接聽）；帶這一通的 ring_id
  const answer = () => {
    const target = doorbellLiveTarget(ring.deviceId, ring.ringId) ?? `/devices/${encodeURIComponent(ring.deviceId)}/live`;
    doorbellRing.dismiss();
    navigate(target);
  };
  const other = doorbellAnswered.get(ring.deviceId);
  const otherBy = other && (!ring.ringId || !other.ringId || other.ringId === ring.ringId) ? other.by : null;
  const view = () => {
    const id = ring.deviceId;
    doorbellRing.dismiss();
    navigate(`/events/${encodeURIComponent(id)}`);
  };

  return (
    <div className="doorbell-scrim" role="dialog" aria-modal="true" aria-label={t('doorbell.ariaLabel')}>
      <div className="doorbell-card" ref={cardRef}>
        <div className="doorbell-pulse" aria-hidden="true">
          <span className="doorbell-icon">🔔</span>
        </div>
        <div className="doorbell-title">{t('doorbell.title')}</div>
        <div className="doorbell-device" title={ring.deviceId}>
          {ring.deviceName}
        </div>
        {otherBy && (
          <div className="doorbell-answered" role="status" data-testid="doorbell-answered">
            {answeredText(t, otherBy, doorbellAnswered.names(), currentUserId())}
          </div>
        )}
        <div className="doorbell-actions">
          <button className="doorbell-btn doorbell-btn--answer" onClick={answer} data-autofocus>
            {t('doorbell.answer')}
          </button>
          <button className="doorbell-btn doorbell-btn--ghost" onClick={view}>
            {t('doorbell.viewEvents')}
          </button>
          <button className="doorbell-btn doorbell-btn--dismiss" onClick={() => doorbellRing.dismiss()}>
            {t('doorbell.dismiss')}
          </button>
        </div>
      </div>
    </div>
  );
}
